import { User, Bot } from 'lucide-react';
import { useMemo, useState } from 'react';
import ComparisonResponseCard from './ComparisonResponseCard';
import { MarkdownMessageContent } from './chat/MarkdownMessageContent';
import { MessageSources } from './chat/MessageSources';
import { CreateTicketButton } from './chat/CreateTicketModal';

function getComparisonColumnWidth(responseCount) {
  if (responseCount <= 1) return '100%';
  if (responseCount === 2) return '47%';
  return '30%';
}

function getMessageTestId(isUser, isComparison) {
  if (isUser) {
    return 'user-message';
  }
  if (isComparison) {
    return 'comparison-message';
  }
  return 'ai-message';
}

function getMessageContainerClass(isUser) {
  return isUser
    ? 'rounded-tr-sm bg-zinc-800 text-white'
    : 'rounded-tl-sm border border-zinc-800 bg-transparent text-zinc-100 shadow-sm';
}

function getComparisonSummaryLabel(responseCount) {
  const pluralSuffix = responseCount > 1 ? 's' : '';
  return `${responseCount} réponse${pluralSuffix} sélectionnée${pluralSuffix}`;
}

function MessageAvatar({ isUser }) {
  if (isUser) {
    return (
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white text-black">
        <User className="h-5 w-5" />
      </div>
    );
  }

  return (
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900">
      <Bot className="h-5 w-5 text-white" />
    </div>
  );
}

function ComparisonColumns({ comparison, preferredModel, votingModelKey, columnWidth, messageId, onPreferenceVote }) {
  return (
    <div className="flex flex-wrap gap-4">
      {comparison.map((response) => (
        <div
          key={response.model_key}
          data-testid={`comparison-column-${response.model_key}`}
          style={{
            flexBasis: columnWidth,
            maxWidth: columnWidth,
            flexGrow: 1,
            minWidth: comparison.length === 1 ? '100%' : '300px',
          }}
        >
          <ComparisonResponseCard
            response={response}
            isPreferred={preferredModel === response.model_key}
            isVoting={votingModelKey === response.model_key}
            onVote={(modelKey) => onPreferenceVote?.(messageId, modelKey)}
          />
        </div>
      ))}
    </div>
  );
}

function AssistantMessageContent({ message, isComparison, votingModelKey, comparisonColumnWidth, onPreferenceVote }) {
  if (!isComparison) {
    return <MarkdownMessageContent content={message.content} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">{getComparisonSummaryLabel(message.comparison.length)}</div>
          <div className="text-xs text-zinc-500">Même question, mêmes sources, affichage adaptatif selon les modèles actifs</div>
        </div>
      </div>
      <ComparisonColumns
        comparison={message.comparison}
        preferredModel={message.preferred_model}
        votingModelKey={votingModelKey}
        columnWidth={comparisonColumnWidth}
        messageId={message.id}
        onPreferenceVote={onPreferenceVote}
      />
      <div className="flex justify-end pt-2">
        <CreateTicketButton messageId={message.id} />
      </div>
    </div>
  );
}

function MessageBubbleBody({ message, isUser, isComparison, votingModelKey, comparisonColumnWidth, onPreferenceVote }) {
  return (
    <div className={`px-6 py-4 rounded-2xl ${getMessageContainerClass(isUser)}`} data-testid={getMessageTestId(isUser, isComparison)}>
      {isUser ? (
        <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
      ) : (
        <AssistantMessageContent
          message={message}
          isComparison={isComparison}
          votingModelKey={votingModelKey}
          comparisonColumnWidth={comparisonColumnWidth}
          onPreferenceVote={onPreferenceVote}
        />
      )}
    </div>
  );
}

function MessageBubbleSources({ isUser, message, isComparison, showSources, setShowSources }) {
  if (isUser) {
    return null;
  }

  return (
    <MessageSources
      sources={message.sources}
      isComparison={isComparison}
      showSources={showSources}
      onToggle={() => setShowSources(!showSources)}
    />
  );
}

export default function MessageBubble({ message, onPreferenceVote, votingModelKey }) {
  const [showSources, setShowSources] = useState(false);
  const isUser = message.role === 'user';
  const isComparison = !isUser && Array.isArray(message.comparison) && message.comparison.length > 0;
  const comparisonColumnWidth = useMemo(
    () => getComparisonColumnWidth(message.comparison?.length || 0),
    [message.comparison]
  );

  return (
    <div className={`flex gap-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser ? <MessageAvatar isUser={false} /> : null}
      <div className={`flex flex-col gap-2 ${isUser ? 'max-w-[80%]' : 'w-full'}`}>
        <MessageBubbleBody
          message={message}
          isUser={isUser}
          isComparison={isComparison}
          votingModelKey={votingModelKey}
          comparisonColumnWidth={comparisonColumnWidth}
          onPreferenceVote={onPreferenceVote}
        />
        <MessageBubbleSources
          isUser={isUser}
          message={message}
          isComparison={isComparison}
          showSources={showSources}
          setShowSources={setShowSources}
        />
      </div>
      {isUser ? <MessageAvatar isUser /> : null}
    </div>
  );
}
